<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import { useAccount } from 'dashboard/composables/useAccount';
import { useFavoriteInboxes } from 'dashboard/composables/useFavoriteInboxes';
import { useFavoriteLabels } from 'dashboard/composables/useFavoriteLabels';
import { useMapGetter } from 'dashboard/composables/store';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'next/icon/Icon.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import {
  filterAndSortConversationFilters,
  groupConversationInboxes,
} from './conversationFilterHelpers';

const props = defineProps({
  filterType: {
    type: String,
    default: 'inboxes',
  },
});

const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { isInboxFavorite, toggleFavoriteInbox } = useFavoriteInboxes();
const { isLabelFavorite, toggleFavoriteLabel } = useFavoriteLabels();
const query = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const inboxUiFlags = useMapGetter('inboxes/getUIFlags');
const labelUiFlags = useMapGetter('labels/getUIFlags');
const failedInboxAvatarIds = ref({});
const getInboxOpenCount = useMapGetter(
  'conversationOpenCounts/getInboxOpenCount'
);
const getLabelOpenCount = useMapGetter(
  'conversationOpenCounts/getLabelOpenCount'
);
const getInboxCount = useMapGetter('conversationOpenCounts/getInboxCount');
const getLabelCount = useMapGetter('conversationOpenCounts/getLabelCount');

const isInboxFilter = computed(() => props.filterType === 'inboxes');
const currentItems = computed(() =>
  isInboxFilter.value ? inboxes.value : labels.value
);
const itemLabel = item => (isInboxFilter.value ? item.name : item.title);
const filteredItems = computed(() =>
  filterAndSortConversationFilters(currentItems.value, query.value, itemLabel)
);
const isLoading = computed(() =>
  isInboxFilter.value
    ? inboxUiFlags.value.isFetching
    : labelUiFlags.value.isFetching
);
const title = computed(() =>
  isInboxFilter.value
    ? t('CONVERSATION_FILTERS.INBOXES_TITLE')
    : t('CONVERSATION_FILTERS.LABELS_TITLE')
);
const groupedItems = computed(() =>
  isInboxFilter.value
    ? groupConversationInboxes(filteredItems.value)
    : [{ key: 'labels', label: title.value, items: filteredItems.value }]
);
const description = computed(() =>
  isInboxFilter.value
    ? t('CONVERSATION_FILTERS.INBOXES_DESCRIPTION')
    : t('CONVERSATION_FILTERS.LABELS_DESCRIPTION')
);
const searchPlaceholder = computed(() =>
  isInboxFilter.value
    ? t('CONVERSATION_FILTERS.SEARCH_INBOXES')
    : t('CONVERSATION_FILTERS.SEARCH_LABELS')
);

const filterRoute = filterType =>
  accountScopedRoute('conversation_filters', { filterType });

const itemRoute = item =>
  isInboxFilter.value
    ? accountScopedRoute('inbox_dashboard', { inbox_id: item.id })
    : accountScopedRoute('label_conversations', { label: item.title });

const itemOpenCount = item =>
  isInboxFilter.value
    ? getInboxOpenCount.value(item.id)
    : getLabelOpenCount.value(item.id);

const itemCount = item =>
  isInboxFilter.value
    ? getInboxCount.value(item.id)
    : getLabelCount.value(item.id);

const hasInboxAvatar = item =>
  Boolean(item.avatar_url) && !failedInboxAvatarIds.value[item.id];

const markInboxAvatarAsFailed = item => {
  failedInboxAvatarIds.value = {
    ...failedInboxAvatarIds.value,
    [item.id]: true,
  };
};

const isItemFavorite = item =>
  isInboxFilter.value ? isInboxFavorite(item.id) : isLabelFavorite(item.id);

const favoriteButtonLabel = item => {
  const translationParams = { label: itemLabel(item) };
  return isItemFavorite(item)
    ? t('CONVERSATION_FILTERS.REMOVE_FAVORITE', translationParams)
    : t('CONVERSATION_FILTERS.ADD_FAVORITE', translationParams);
};

const toggleFavorite = item =>
  isInboxFilter.value
    ? toggleFavoriteInbox(item.id)
    : toggleFavoriteLabel(item.id);

watch(
  () => props.filterType,
  () => {
    query.value = '';
  }
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('conversationOpenCounts/get');
});
</script>

<template>
  <main class="flex-1 overflow-y-auto bg-n-background">
    <div class="w-full max-w-6xl px-4 py-6 mx-auto sm:px-6 lg:px-8">
      <header class="flex flex-col gap-5 mb-6">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">{{ title }}</h1>
          <p class="mt-1 text-sm text-n-slate-11">{{ description }}</p>
        </div>

        <nav
          class="flex gap-1 p-1 rounded-lg bg-n-alpha-2 w-fit"
          :aria-label="t('CONVERSATION_FILTERS.TABS_LABEL')"
        >
          <router-link
            :to="filterRoute('inboxes')"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="
              isInboxFilter
                ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
            "
          >
            <Icon icon="i-lucide-mailbox" class="size-4" />
            {{ t('SIDEBAR.CHANNELS') }}
            <span class="text-xs text-n-slate-10">{{ inboxes.length }}</span>
          </router-link>
          <router-link
            :to="filterRoute('labels')"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="
              !isInboxFilter
                ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
            "
          >
            <Icon icon="i-lucide-tag" class="size-4" />
            {{ t('SIDEBAR.LABELS') }}
            <span class="text-xs text-n-slate-10">{{ labels.length }}</span>
          </router-link>
        </nav>

        <div class="relative max-w-xl">
          <Icon
            icon="i-lucide-search"
            class="absolute z-10 -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-n-slate-10"
          />
          <Input
            id="conversation-filter-search"
            v-model="query"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            custom-input-class="!pl-9"
            autofocus
          />
        </div>
      </header>

      <div
        v-if="isLoading && !currentItems.length"
        class="flex items-center justify-center py-20 text-n-slate-11"
      >
        <Icon icon="i-lucide-loader-circle" class="mr-2 size-5 animate-spin" />
        {{ t('CONVERSATION_FILTERS.LOADING') }}
      </div>

      <div
        v-else-if="!filteredItems.length"
        class="flex flex-col items-center justify-center px-4 py-20 text-center border border-dashed rounded-xl border-n-weak"
      >
        <Icon icon="i-lucide-search-x" class="mb-3 size-8 text-n-slate-9" />
        <p class="font-medium text-n-slate-12">
          {{ t('CONVERSATION_FILTERS.EMPTY_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CONVERSATION_FILTERS.EMPTY_DESCRIPTION') }}
        </p>
      </div>

      <div v-else class="flex flex-col gap-7">
        <section
          v-for="group in groupedItems"
          :key="group.key"
          :aria-labelledby="`connection-group-${group.key}`"
        >
          <header v-if="isInboxFilter" class="mb-3 flex items-center gap-2">
            <Icon :icon="group.icon" class="size-4 text-n-slate-10" />
            <h2
              :id="`connection-group-${group.key}`"
              class="text-sm font-semibold text-n-slate-12"
            >
              {{ group.label }}
            </h2>
            <span
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-10"
            >
              {{ group.items.length }}
            </span>
          </header>

          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="item in group.items"
              :key="item.id"
              class="group relative flex min-w-0 items-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3.5 transition-colors hover:bg-n-alpha-2"
              :class="
                isInboxFilter
                  ? 'hover:border-n-brand'
                  : 'label-filter-card border-l-4'
              "
              :style="
                isInboxFilter
                  ? undefined
                  : {
                      '--label-color': item.color,
                      borderLeftColor: item.color,
                    }
              "
            >
              <router-link
                :to="itemRoute(item)"
                class="absolute inset-0 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :aria-label="itemLabel(item)"
              />

              <span
                v-if="isInboxFilter"
                class="flex items-center justify-center overflow-hidden rounded-lg size-9 shrink-0 bg-n-alpha-2"
              >
                <img
                  v-if="hasInboxAvatar(item)"
                  :src="item.avatar_url"
                  :alt="itemLabel(item)"
                  class="object-cover size-full"
                  @error="markInboxAvatarAsFailed(item)"
                />
                <ChannelIcon
                  v-else
                  :inbox="item"
                  colored
                  use-brand-icon
                  class="size-5"
                />
              </span>

              <span class="min-w-0 flex-1">
                <span
                  class="block text-sm font-medium truncate text-n-slate-12"
                >
                  {{ itemLabel(item) }}
                </span>
                <span class="mt-1 flex flex-wrap items-center gap-1 text-xs">
                  <span class="font-medium text-n-slate-11">
                    {{
                      t('CONVERSATION_FILTERS.TOTAL_CONVERSATIONS', {
                        count: itemCount(item),
                      })
                    }}
                  </span>
                  <span
                    class="inline-flex items-center border-l border-n-weak pl-1 text-n-slate-9"
                  >
                    {{
                      t('CONVERSATION_FILTERS.OPEN_COUNT', {
                        count: itemOpenCount(item),
                      })
                    }}
                  </span>
                </span>
              </span>

              <button
                type="button"
                class="relative z-10 flex items-center justify-center rounded-lg size-8 shrink-0 transition-colors hover:bg-n-alpha-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :class="
                  isItemFavorite(item)
                    ? 'text-n-amber-9'
                    : 'text-n-slate-9 hover:text-n-amber-9'
                "
                :title="favoriteButtonLabel(item)"
                :aria-label="favoriteButtonLabel(item)"
                :aria-pressed="isItemFavorite(item)"
                @click="toggleFavorite(item)"
              >
                <Icon
                  :icon="
                    isItemFavorite(item) ? 'i-ri-star-fill' : 'i-lucide-star'
                  "
                  class="size-4"
                />
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </main>
</template>

<style scoped>
.label-filter-card:hover {
  border-color: var(--label-color);
}
</style>
